import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { type StatoTone } from "@/features/ordini/components/statoToni";
import { prettifyApiError } from "../../shared/api";
import { propsSenzaPermesso, usePermessi } from "../../shared/auth/permessi";
import { ErrorBanner } from "../../shared/ui/ErrorBanner";
import { CampoCerca, ChipFiltro } from "../../shared/ui/ChipFiltro";
import { PageHeader } from "../../shared/ui/PageHeader";
import { FatturaEsteraRiga } from "./components/FatturaEsteraRiga";
import {
  correggiFattura,
  creaAut,
  eliminaFattura,
  fetchFattureEstere,
  rileggiPosta,
  segnaAutofattura,
  type Correzione,
  type ElencoFattureEstere,
  type FatturaEstera,
} from "./fattureEstereApi";

type Vista = "da_fare" | "fatte" | "tutte";

const VISTE: Array<{ chiave: Vista; etichetta: string; tono: StatoTone }> = [
  { chiave: "da_fare", etichetta: "Da fare", tono: "da-pagare" },
  { chiave: "fatte", etichetta: "Fatte", tono: "pagato" },
  { chiave: "tutte", etichetta: "Tutte", tono: "annullato" },
];

const nomeMese = (chiave: string) => {
  const [anno, mese] = chiave.split("-").map(Number);
  const testo = new Date(anno ?? 2000, (mese ?? 1) - 1, 1).toLocaleDateString("it-IT", {
    month: "long",
    year: "numeric",
  });
  return testo.charAt(0).toUpperCase() + testo.slice(1);
};

const quando = (iso: string) =>
  new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

function corrisponde(f: FatturaEstera, testo: string): boolean {
  if (!testo) return true;
  const ago = testo.toLowerCase();
  return [f.fornitore_nome, f.numero, f.oggetto, f.mittente, f.nota, f.importo]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(ago));
}

/** Righe finte del caricamento: stessa altezza delle vere, niente salto. */
function Scheletro() {
  return (
    <div aria-hidden="true" className="rounded-lg border border-line bg-card">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex items-center gap-4 border-b border-line px-4 py-4 last:border-b-0"
        >
          <div className="h-8 w-8 rounded-full bg-paper-2" />
          <div className="h-3.5 w-40 animate-pulse rounded bg-paper-2" />
          <div className="h-3 w-24 animate-pulse rounded bg-paper-2" />
          <div className="ml-auto h-7 w-16 animate-pulse rounded-full bg-paper-2" />
        </div>
      ))}
    </div>
  );
}

export function FattureEsterePage() {
  const [dati, setDati] = useState<ElencoFattureEstere | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>("da_fare");
  const [fornitore, setFornitore] = useState("");
  const [cerca, setCerca] = useState("");
  const [rileggo, setRileggo] = useState<"no" | "in_corso" | "accodato" | "gia">("no");
  const { puoScrivere } = usePermessi();
  const puo = puoScrivere("fatturazione");
  const senzaPermesso = propsSenzaPermesso(puo, "fatturazione");

  const ricarica = useCallback(async (signal?: AbortSignal) => {
    try {
      setDati(await fetchFattureEstere(signal));
      setErrore(null);
    } catch (err) {
      if (signal?.aborted) return;
      setErrore(prettifyApiError(err));
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void ricarica(ctrl.signal);
    return () => ctrl.abort();
  }, [ricarica]);

  const fatture = useMemo(() => dati?.fatture ?? [], [dati]);
  const daFare = fatture.filter((f) => f.autofattura_il === null).length;
  const conteggi: Record<Vista, number> = {
    da_fare: daFare,
    fatte: fatture.length - daFare,
    tutte: fatture.length,
  };

  const visibili = useMemo(
    () =>
      fatture.filter(
        (f) =>
          (vista === "tutte" || (vista === "fatte") === (f.autofattura_il !== null)) &&
          (!fornitore || f.fornitore === fornitore) &&
          corrisponde(f, cerca.trim()),
      ),
    [fatture, vista, fornitore, cerca],
  );

  // Per mese della fattura: Annalisa lavora a mese, e le autofatture hanno la
  // data del mese in cui la fattura e' arrivata.
  const perMese = useMemo(() => {
    const gruppi = new Map<string, FatturaEstera[]>();
    for (const f of visibili) {
      const chiave = f.data_documento.slice(0, 7);
      gruppi.set(chiave, [...(gruppi.get(chiave) ?? []), f]);
    }
    return [...gruppi.entries()];
  }, [visibili]);

  const fornitoriPresenti = useMemo(() => {
    const chiavi = new Set(fatture.map((f) => f.fornitore));
    return (dati?.fornitori ?? []).filter((f) => chiavi.has(f.chiave));
  }, [dati, fatture]);

  async function rileggi() {
    setRileggo("in_corso");
    try {
      const esito = await rileggiPosta();
      setRileggo(esito.accodato ? "accodato" : "gia");
    } catch (err) {
      setRileggo("no");
      setErrore(prettifyApiError(err));
    }
  }

  async function spunta(f: FatturaEstera, fatta: boolean) {
    await segnaAutofattura(f.id, fatta);
    await ricarica();
  }

  async function correggi(f: FatturaEstera, corpo: Correzione) {
    await correggiFattura(f.id, corpo);
    await ricarica();
  }

  async function crea(f: FatturaEstera) {
    // Ricarica anche se Eureka risponde male: il motivo e l'errore della riga
    // cambiano comunque (il tentativo e' segnato prima della chiamata).
    try {
      await creaAut(f.id);
    } finally {
      await ricarica();
    }
  }

  async function elimina(f: FatturaEstera) {
    await eliminaFattura(f.id);
    await ricarica();
  }

  const giro = dati?.ultimo_giro ?? null;
  const saltate = dati?.caselle.saltate ?? [];

  return (
    <main className="flex-1 overflow-y-auto px-7 pt-[22px] pb-8 scroll-paper max-md:px-3 max-md:pt-3">
      <PageHeader
        title="Fatture estere"
        subtitle="Le fatture dei fornitori esteri, pronte per le autofatture. Quelle che hanno già la AUT in Eureka si spuntano da sole."
        meta={
          <div className="flex flex-col items-end gap-1 max-md:items-start">
            <Link
              to="/fatturazione"
              className="font-semibold text-ink hover:underline max-md:hidden"
            >
              ← Fatturazione
            </Link>
            {giro ? (
              <>
                <span className={giro.ok ? "" : "font-semibold text-red"}>
                  Posta letta {quando(giro.quando)}
                  {giro.ok ? "" : " — non riuscita"}
                </span>
                {giro.eureka &&
                  (giro.eureka.errore ? (
                    <span className="font-semibold text-red">
                      Controllo Eureka non riuscito: {giro.eureka.errore}
                    </span>
                  ) : (
                    <span>Autofatture cercate in Eureka</span>
                  ))}
              </>
            ) : (
              dati && <span>Posta non ancora letta</span>
            )}
          </div>
        }
      />

      <ErrorBanner message={errore} className="mb-3" />
      {giro && !giro.ok && giro.errore && (
        <ErrorBanner message={`Ultima lettura della posta: ${giro.errore}`} className="mb-3" />
      )}
      {saltate.length > 0 && (
        <div
          role="status"
          className="mb-4 rounded-md border border-amber/30 bg-amber-soft px-3.5 py-2.5 text-[13px] leading-snug text-ink"
        >
          <strong>Casella non collegata: {saltate.join(", ")}.</strong> Li' arrivano le fatture di
          Shopify, Google, Meta, Hostinger e Anthropic: finche' non e' collegata, queste non entrano
          da sole.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2 max-md:flex-nowrap max-md:overflow-x-auto max-md:pb-1">
        {VISTE.map((v) => (
          <ChipFiltro
            key={v.chiave}
            tono={v.tono}
            attivo={vista === v.chiave}
            onClick={() => setVista(v.chiave)}
            conteggio={dati ? conteggi[v.chiave] : undefined}
          >
            {v.etichetta}
          </ChipFiltro>
        ))}
        <select
          aria-label="Fornitore"
          value={fornitore}
          onChange={(e) => setFornitore(e.target.value)}
          className="h-[30px] rounded-full border border-line-2 bg-card px-3 text-[12.5px] font-semibold text-ink outline-none focus-visible:ring-2 focus-visible:ring-ink-4 max-md:h-10 max-md:shrink-0 max-md:text-[14px]"
        >
          <option value="">Tutti i fornitori</option>
          {fornitoriPresenti.map((f) => (
            <option key={f.chiave} value={f.chiave}>
              {f.nome}
            </option>
          ))}
        </select>
        <CampoCerca
          value={cerca}
          onChange={setCerca}
          placeholder="Cerca numero, fornitore, importo"
          ariaLabel="Cerca fra le fatture estere"
          className="w-[260px] max-md:hidden"
        />
        <button
          type="button"
          onClick={() => void rileggi()}
          disabled={!puo || rileggo === "in_corso"}
          title={
            puo
              ? "Legge subito la posta invece di aspettare il giro di ogni ora"
              : senzaPermesso.title
          }
          className="ml-auto inline-flex h-[30px] items-center gap-1.5 rounded-full border border-line-2 bg-card px-3 text-[12.5px] font-semibold text-ink transition-colors hover:border-ink-3 hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-10 max-md:shrink-0 max-md:text-[14px]"
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className={`h-[14px] w-[14px] fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round] ${rileggo === "in_corso" ? "animate-spin" : ""}`}
          >
            <path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v7h-7" />
          </svg>
          Rileggi la posta
        </button>
      </div>
      <CampoCerca
        value={cerca}
        onChange={setCerca}
        placeholder="Cerca numero, fornitore, importo"
        ariaLabel="Cerca fra le fatture estere"
        className="mb-4 hidden max-md:flex"
      />
      {rileggo === "accodato" && (
        <p role="status" className="-mt-2 mb-3 text-[12.5px] text-ink-2">
          Lettura avviata: fra un minuto ricarica la pagina.
        </p>
      )}
      {rileggo === "gia" && (
        <p role="status" className="-mt-2 mb-3 text-[12.5px] text-ink-2">
          Una lettura e' gia' in corso.
        </p>
      )}

      <div className="min-w-0">
        {dati === null && !errore && <Scheletro />}
        {dati !== null && perMese.length === 0 && (
          <div className="rounded-lg border border-dashed border-line-2 px-6 py-12 text-center">
            <p className="font-display text-[18px] font-bold text-ink">
              {fatture.length === 0
                ? "Ancora nessuna fattura estera"
                : vista === "da_fare" && !fornitore && !cerca
                  ? "Nessuna autofattura da fare"
                  : "Nessuna fattura con questi filtri"}
            </p>
            <p className="mt-1 text-[13px] text-ink-2">
              {fatture.length === 0
                ? "La posta si legge da sola ogni ora: le fatture arrivano qui senza fare niente."
                : vista === "da_fare" && !fornitore && !cerca
                  ? "Tutte quelle arrivate sono gia' segnate."
                  : "Prova un altro fornitore o togli la ricerca."}
            </p>
          </div>
        )}
        {perMese.map(([mese, righe]) => {
          const aperte = righe.filter((r) => r.autofattura_il === null).length;
          return (
            <section key={mese} aria-labelledby={`mese-${mese}`} className="mb-6 last:mb-0">
              <div className="mb-2 flex items-baseline justify-between gap-3 px-1">
                <h2 id={`mese-${mese}`} className="font-display text-[18px] font-bold text-ink">
                  {nomeMese(mese)}
                </h2>
                <span className="text-[12.5px] tabular-nums text-ink-2">
                  {righe.length} {righe.length === 1 ? "fattura" : "fatture"}
                  {aperte > 0 && vista !== "da_fare" && (
                    <span className="font-semibold text-red"> · {aperte} da fare</span>
                  )}
                </span>
              </div>
              <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-card">
                {righe.map((f) => (
                  <FatturaEsteraRiga
                    key={f.id}
                    fattura={f}
                    puoScrivere={puo}
                    titoloSenzaPermesso={senzaPermesso.title}
                    onSpunta={spunta}
                    onCorreggi={correggi}
                    onElimina={elimina}
                    onCreaAut={crea}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </main>
  );
}
